import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import OperatorLayout from "./OperatorLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Star, Send, Settings, MessageSquare, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

type Tab = "requests" | "config";

const statusColors: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  sent: "bg-accent/10 text-accent",
  completed: "bg-primary/10 text-primary",
  expired: "bg-destructive/10 text-destructive",
};

const OperatorFeedback = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>("requests");
  const queryClient = useQueryClient();

  const { data: feedbackRequests = [], isLoading: loadingRequests } = useQuery({
    queryKey: ["operator-feedback-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("feedback_requests")
        .select("*, provider:provider_profiles(company_name), opportunity:opportunities(opportunity_number)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const { data: wonOpportunities = [] } = useQuery({
    queryKey: ["operator-won-without-feedback"],
    queryFn: async () => {
      const { data: opps, error } = await supabase
        .from("opportunities")
        .select("id, opportunity_number, provider_id, provider:provider_profiles(company_name), request:requests(requester_email, requester_company)")
        .eq("status", "completed").eq("outcome", "won").order("completed_at", { ascending: false });
      if (error) throw error;
      const { data: existing } = await supabase.from("feedback_requests").select("opportunity_id");
      const existingIds = new Set((existing || []).map((e: any) => e.opportunity_id));
      return (opps || []).filter((o: any) => !existingIds.has(o.id));
    },
  });

  const createAndSend = useMutation({
    mutationFn: async (opportunityId: string) => {
      const { data: createData, error: createErr } = await supabase.functions.invoke("send-feedback-email", { body: { action: "create", opportunity_id: opportunityId } });
      if (createErr) throw createErr;
      if (createData?.error && createData?.id) {
        const { error: sendErr } = await supabase.functions.invoke("send-feedback-email", { body: { action: "send", feedback_request_id: createData.id } });
        if (sendErr) throw sendErr;
        return;
      }
      if (createData?.error) throw new Error(createData.error);
      const { error: sendErr } = await supabase.functions.invoke("send-feedback-email", { body: { action: "send", feedback_request_id: createData.id } });
      if (sendErr) throw sendErr;
    },
    onSuccess: () => {
      toast.success(t("op.fbEmailSent"));
      queryClient.invalidateQueries({ queryKey: ["operator-feedback-requests"] });
      queryClient.invalidateQueries({ queryKey: ["operator-won-without-feedback"] });
    },
    onError: (err: any) => toast.error(err.message || t("common.error")),
  });

  const resend = useMutation({
    mutationFn: async (feedbackRequestId: string) => {
      const { error } = await supabase.functions.invoke("send-feedback-email", { body: { action: "send", feedback_request_id: feedbackRequestId } });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("op.fbEmailResent"));
      queryClient.invalidateQueries({ queryKey: ["operator-feedback-requests"] });
    },
  });

  const { data: configs = [], isLoading: loadingConfigs } = useQuery({
    queryKey: ["feedback-configs"],
    queryFn: async () => {
      const { data, error } = await supabase.from("feedback_configs").select("*").order("sort_order");
      if (error) throw error;
      return data || [];
    },
  });

  const [editingConfig, setEditingConfig] = useState<any>(null);
  const [isCreating, setIsCreating] = useState(false);

  const createConfig = useMutation({
    mutationFn: async (config: { question_key: string; question_text: string; question_type: string; sort_order: number; is_active: boolean }) => {
      const { error } = await supabase.from("feedback_configs").insert(config);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("op.fbSaved"));
      setEditingConfig(null);
      setIsCreating(false);
      queryClient.invalidateQueries({ queryKey: ["feedback-configs"] });
    },
    onError: (err: any) => toast.error(err.message || t("common.error")),
  });

  const updateConfig = useMutation({
    mutationFn: async (config: any) => {
      const { error } = await supabase.from("feedback_configs")
        .update({ question_text: config.question_text, question_type: config.question_type, is_active: config.is_active, sort_order: config.sort_order })
        .eq("id", config.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("op.fbSaved"));
      setEditingConfig(null);
      queryClient.invalidateQueries({ queryKey: ["feedback-configs"] });
    },
  });

  const [viewingResponses, setViewingResponses] = useState<string | null>(null);
  const { data: responses = [] } = useQuery({
    queryKey: ["feedback-responses", viewingResponses],
    queryFn: async () => {
      if (!viewingResponses) return [];
      const { data, error } = await supabase.from("feedback_responses").select("*").eq("feedback_request_id", viewingResponses).order("created_at");
      if (error) throw error;
      return data || [];
    },
    enabled: !!viewingResponses,
  });

  return (
    <OperatorLayout title={t("op.fbTitle")} subtitle={t("op.fbSubtitle")}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-headline">{t("op.fbTitle")}</h1>
          <div className="flex gap-2">
            <Button variant={activeTab === "requests" ? "commit" : "quiet"} size="sm" onClick={() => setActiveTab("requests")}>
              <MessageSquare className="w-4 h-4 mr-1" /> {t("op.fbRequests")}
            </Button>
            <Button variant={activeTab === "config" ? "commit" : "quiet"} size="sm" onClick={() => setActiveTab("config")}>
              <Settings className="w-4 h-4 mr-1" /> {t("op.fbConfig")}
            </Button>
          </div>
        </div>

        {activeTab === "requests" && (
          <div className="space-y-6">
            {wonOpportunities.length > 0 && (
              <div className="p-4 border border-accent/20 bg-accent/5">
                <h3 className="text-micro mb-3">{t("op.fbWonWithout", { count: wonOpportunities.length })}</h3>
                <div className="space-y-2">
                  {wonOpportunities.map((opp: any) => (
                    <div key={opp.id} className="flex items-center justify-between p-3 bg-card border border-border">
                      <div>
                        <span className="text-body font-medium">{opp.opportunity_number}</span>
                        <span className="text-caption ml-2">{(opp as any).provider?.company_name}</span>
                        <span className="text-micro ml-2">{(opp as any).request?.requester_email}</span>
                      </div>
                      <Button variant="commit" size="sm" onClick={() => createAndSend.mutate(opp.id)} disabled={createAndSend.isPending}>
                        <Send className="w-3 h-3 mr-1" /> {t("op.fbSend")}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-micro mb-3">{t("op.fbAllRequests")}</h3>
              {loadingRequests ? (
                <p className="text-caption">{t("common.loading")}</p>
              ) : feedbackRequests.length === 0 ? (
                <p className="text-caption">{t("op.fbNoRequests")}</p>
              ) : (
                <div className="space-y-2">
                  {feedbackRequests.map((fr: any) => (
                    <div key={fr.id} className="flex items-center justify-between p-4 border border-border bg-card">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-body font-medium">{(fr as any).opportunity?.opportunity_number}</span>
                          <Badge className={statusColors[fr.status] || ""}>{fr.status}</Badge>
                        </div>
                        <p className="text-caption">{(fr as any).provider?.company_name} → {fr.customer_email}</p>
                        {fr.sent_at && <p className="text-micro">{t("op.fbSentAt", { date: new Date(fr.sent_at).toLocaleString() })}</p>}
                      </div>
                      <div className="flex gap-2">
                        {fr.status === "completed" && (
                          <Button variant="quiet" size="sm" onClick={() => setViewingResponses(fr.id)}>
                            <Star className="w-3 h-3 mr-1" /> {t("op.fbResponses")}
                          </Button>
                        )}
                        {fr.status !== "completed" && (
                          <Button variant="quiet" size="sm" onClick={() => resend.mutate(fr.id)} disabled={resend.isPending}>
                            <Send className="w-3 h-3 mr-1" /> {t("op.fbResend")}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "config" && (
          <div className="space-y-4">
            <p className="text-caption">{t("op.fbConfigDesc")}</p>
            <Button variant="commit" size="sm" onClick={() => { setIsCreating(true); setEditingConfig({ question_key: "", question_text: "", question_type: "rating", sort_order: (configs.length + 1) * 10, is_active: true }); }}>
              <Plus className="w-4 h-4 mr-1" /> {t("op.fbNewQuestion")}
            </Button>
            {loadingConfigs ? (
              <p className="text-caption">{t("common.loading")}</p>
            ) : (
              <div className="space-y-2">
                {configs.map((c: any) => (
                  <div key={c.id} className="flex items-center justify-between p-4 border border-border bg-card">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-body">{c.question_text}</span>
                        <Badge variant="outline">{c.question_type}</Badge>
                        {!c.is_active && <Badge variant="secondary">{t("op.pricInactive")}</Badge>}
                      </div>
                      <p className="text-micro mt-1">Key: {c.question_key} · {t("op.pricSort")}: {c.sort_order}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setIsCreating(false); setEditingConfig({ ...c }); }}>{t("op.pricEdit")}</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={!!editingConfig} onOpenChange={(v) => { if (!v) { setEditingConfig(null); setIsCreating(false); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isCreating ? t("op.fbNewQuestion") : t("op.fbEditQuestion")}</DialogTitle></DialogHeader>
          {editingConfig && (
            <div className="space-y-4 mt-4">
              {isCreating && (
                <div>
                  <label className="text-micro block mb-1">Key</label>
                  <Input value={editingConfig.question_key} onChange={(e) => setEditingConfig({ ...editingConfig, question_key: e.target.value.replace(/[^a-z0-9_]/g, "") })} placeholder={t("op.fbKeyPlaceholder")} />
                </div>
              )}
              <div>
                <label className="text-micro block mb-1">{t("op.fbQuestionText")}</label>
                <Textarea value={editingConfig.question_text} onChange={(e) => setEditingConfig({ ...editingConfig, question_text: e.target.value })} />
              </div>
              <div>
                <label className="text-micro block mb-1">{t("op.fbType")}</label>
                <select className="w-full border border-border bg-background p-2 text-sm" value={editingConfig.question_type}
                  onChange={(e) => setEditingConfig({ ...editingConfig, question_type: e.target.value })}>
                  <option value="rating">{t("op.fbTypeRating")}</option>
                  <option value="text">{t("op.fbTypeText")}</option>
                  <option value="yes_no">{t("op.fbTypeYesNo")}</option>
                  <option value="scale">{t("op.fbTypeScale")}</option>
                </select>
              </div>
              <div>
                <label className="text-micro block mb-1">{t("op.pricSort")}</label>
                <Input type="number" value={editingConfig.sort_order} onChange={(e) => setEditingConfig({ ...editingConfig, sort_order: parseInt(e.target.value) || 0 })} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={editingConfig.is_active} onCheckedChange={(v) => setEditingConfig({ ...editingConfig, is_active: v })} />
                <span className="text-caption">{t("op.pricActiveLabel")}</span>
              </div>
              <Button variant="commit" className="w-full"
                onClick={() => isCreating ? createConfig.mutate(editingConfig) : updateConfig.mutate(editingConfig)}
                disabled={createConfig.isPending || updateConfig.isPending || (isCreating && !editingConfig.question_key)}>
                {t("common.save")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewingResponses} onOpenChange={(v) => !v && setViewingResponses(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("op.fbResponses")}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {responses.length === 0 ? (
              <p className="text-caption">{t("op.fbNoResponses")}</p>
            ) : (
              responses.map((r: any) => (
                <div key={r.id} className="p-3 border border-border">
                  <p className="text-micro mb-1">{r.question_key}</p>
                  {r.rating !== null && (
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`w-4 h-4 ${s <= r.rating ? "text-accent fill-accent" : "text-muted-foreground/30"}`} />
                      ))}
                    </div>
                  )}
                  {r.text_value && <p className="text-caption mt-1">{r.text_value}</p>}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </OperatorLayout>
  );
};

export default OperatorFeedback;
